import React, { useMemo } from 'react';
import { ASINSalesHistoryRow } from '@/types/asinSalesHistory';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { BarChart3 } from 'lucide-react';
import { ASINLeaderboard } from './ASINLeaderboard';

interface PerformanceChartsProps {
  data: ASINSalesHistoryRow[];
  loading: boolean;
  performanceFilter: 'top_10' | 'bottom_10';
  filterType: 'date' | 'asin';
  selectedASIN?: string | null;
}

export const PerformanceCharts: React.FC<PerformanceChartsProps> = ({
  data,
  loading,
  performanceFilter,
  filterType,
  selectedASIN
}) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Calculate summary data for ASIN Leaderboard summary card
  const summaryData = useMemo(() => {
    if (!data.length) return null;

    // Filter data based on selected ASIN if any
    const filteredData = selectedASIN ? data.filter(row => row.asin === selectedASIN) : data;
    
    if (!filteredData.length) return null;

    const totalTraffic = filteredData.reduce((sum, row) => sum + row.traffic, 0);
    const totalUnits = filteredData.reduce((sum, row) => sum + row.units, 0);
    const totalRevenue = filteredData.reduce((sum, row) => sum + row.revenue, 0);
    const totalPPCSpend = filteredData.reduce((sum, row) => sum + row.ppcSpend, 0);
    const totalGrossProfit = filteredData.reduce((sum, row) => sum + row.grossProfit, 0);
    
    // Calculate average conversion rate weighted by traffic
    const totalConversions = filteredData.reduce((sum, row) => sum + (row.traffic * (row.conversionRate / 100)), 0);
    const averageConversionRate = totalTraffic > 0 ? (totalConversions / totalTraffic) * 100 : 0;

    // Create 30-day trend data for sparkline
    const trendData = filteredData.slice(-30).map(row => row.grossProfit);

    return {
      totalTraffic,
      averageConversionRate,
      totalUnits,
      totalRevenue,
      totalPPCSpend,
      totalGrossProfit,
      trendData,
      title: selectedASIN ? `ASIN: ${selectedASIN}` : 'All ASINs'
    };
  }, [data, selectedASIN]);

  const MiniSparkline = ({ data }: { data: number[] }) => {
    if (!data.length) return <div className="w-20 h-8 bg-gray-100 dark:bg-gray-800 rounded"></div>;
    
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;
    
    return (
      <div className="w-20 h-8 flex items-end space-x-0.5">
        {data.slice(-15).map((value, index) => {
          const height = Math.max(2, ((value - min) / range) * 28);
          return (
            <div
              key={index}
              className="bg-blue-500 rounded-sm flex-1"
              style={{ height: `${height}px` }}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* (a) ASIN Leaderboard Summary Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center space-x-2">
            <BarChart3 className="h-5 w-5" />
            <span>ASIN Leaderboard</span>
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            {summaryData?.title || 'All ASINs'}
          </p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="grid grid-cols-7 gap-4">
              {[...Array(7)].map((_, i) => (
                <div key={i} className="space-y-2 text-center">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-6 w-full" />
                </div>
              ))}
            </div>
          ) : summaryData ? (
            <div className="grid grid-cols-7 gap-4">
              <div className="text-center space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Traffic</p>
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{formatNumber(summaryData.totalTraffic)}</p>
              </div>
              
              <div className="text-center space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Conversion Rate</p>
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{formatPercent(summaryData.averageConversionRate)}</p>
              </div>
              
              <div className="text-center space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Units</p>
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{formatNumber(summaryData.totalUnits)}</p>
              </div>
              
              <div className="text-center space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Revenue</p>
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{formatCurrency(summaryData.totalRevenue)}</p>
              </div>
              
              <div className="text-center space-y-1">
                <p className="text-xs font-medium text-muted-foreground">PPC Spend</p>
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{formatCurrency(summaryData.totalPPCSpend)}</p>
              </div>
              
              <div className="text-center space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Gross Profit</p>
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{formatCurrency(summaryData.totalGrossProfit)}</p>
              </div>

              <div className="text-center space-y-1">
                <p className="text-xs font-medium text-muted-foreground">30d Trend</p>
                <div className="flex justify-center">
                  {selectedASIN ? (
                    <MiniSparkline data={summaryData.trendData} />
                  ) : (
                    <p className="text-lg font-bold text-blue-600">N/A</p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-muted-foreground">
              No data available for the selected filters
            </div>
          )}
        </CardContent>
      </Card>

      {/* (b) ASIN Leaderboard Table - Raw daily data exactly like Google Sheet */}
      <ASINLeaderboard
        data={data}
        loading={loading}
      />
    </div>
  );
};